import {
  CardBody,
  CardDescription,
  CardHeader,
  CardRoot,
  CardTitle,
  SimpleGrid,
  Stack,
} from '@chakra-ui/react';
import * as React from 'react';

import { LayoutAdminGrid } from './layout-admin-grid';
import MessagePreviewItem, {
  type MessageItem,
  type MessagePrefixProps,
} from './message-preview';

type EditHeaderObject = {
  title: string;
  description?: string;
};

const isEditHeaderObject = (obj: unknown): obj is EditHeaderObject =>
  typeof obj === 'object' && obj !== null && 'title' in obj;

interface LayoutAdminMessagePreviewProps extends MessagePrefixProps {
  editHeader: React.ReactNode | EditHeaderObject;
  editContent: React.ReactNode;
  previewTitle?: string;
  previewDescription?: string;
  messages?: MessageItem[];
  /**
   * Constrain both cards to the available viewport height and scroll the
   * card bodies internally instead of letting the page scroll. Pair with
   * `LayoutAdminPage` `childrenScroll={false}` (the default).
   */
  cardsFillHeight?: boolean;
  dataTestId?: string;
}

export const LayoutAdminMessagePreview: React.FC<
  LayoutAdminMessagePreviewProps
> = ({
  editHeader,
  editContent,
  previewTitle,
  previewDescription,
  messages,
  systemPrefix,
  namePrefix,
  orgPrefix,
  titlePrefix,
  caseIdPrefix,
  phonePrefix,
  separator,
  pointOfView,
  cardsFillHeight,
  dataTestId,
}) => {
  const cardProps = cardsFillHeight
    ? {
        h: 'full',
        display: 'flex' as const,
        flexDirection: 'column' as const,
        minH: 0,
        overflow: 'hidden' as const,
      }
    : {};
  const bodyProps = cardsFillHeight
    ? { flex: '1', minH: 0, overflowY: 'auto' as const }
    : {};

  const editCard = (
    <CardRoot size="md" {...cardProps}>
      <CardHeader>
        {(() => {
          if (React.isValidElement(editHeader)) {
            return editHeader;
          }
          if (isEditHeaderObject(editHeader)) {
            return (
              <Stack gap={0} justify="stretch" align="start">
                <CardTitle>{editHeader.title}</CardTitle>
                {editHeader.description && (
                  <CardDescription>{editHeader.description}</CardDescription>
                )}
              </Stack>
            );
          }
          return null;
        })()}
      </CardHeader>
      <CardBody {...bodyProps}>{editContent}</CardBody>
    </CardRoot>
  );

  const previewCard = (
    <CardRoot size="md" {...cardProps}>
      <CardHeader>
        <Stack gap={0} justify="stretch" align="start">
          <CardTitle>{previewTitle}</CardTitle>
          <CardDescription>{previewDescription}</CardDescription>
        </Stack>
      </CardHeader>
      <CardBody {...bodyProps}>
        <MessagePreviewItem
          messages={messages || []}
          systemPrefix={systemPrefix}
          orgPrefix={orgPrefix}
          namePrefix={namePrefix}
          titlePrefix={titlePrefix}
          caseIdPrefix={caseIdPrefix}
          phonePrefix={phonePrefix}
          separator={separator}
          pointOfView={pointOfView}
        />
      </CardBody>
    </CardRoot>
  );

  if (cardsFillHeight) {
    return (
      <SimpleGrid
        columns={{ base: 1, lg: 2 }}
        gap={{ base: 4, lg: 6 }}
        h="full"
        minH={0}
        overflow="hidden"
        autoRows="1fr"
        data-testid={dataTestId}
      >
        {editCard}
        {previewCard}
      </SimpleGrid>
    );
  }

  return (
    <LayoutAdminGrid
      columns={{ base: 1, lg: 2 }}
      gap={{ base: 4, lg: 6 }}
      dataTestId={dataTestId}
    >
      {editCard}
      {previewCard}
    </LayoutAdminGrid>
  );
};

export default LayoutAdminMessagePreview;
